import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch } from 'react-redux';
import { getBookingInvoice } from '../../../redux/actions/mindfulness';
import { 
  FaCheckCircle, 
  FaCalendarAlt, 
  FaClock, 
  FaComment, 
  FaHourglassHalf,
  FaDownload,
  FaUser,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  font-weight: 400;
  font-family: Montserrat;

  /* Hide scrollbar - cross-browser */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Webkit */
  }
`;

const Header = styled.div`
  text-align: center;
  padding: 20px 24px 24px;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #10b981;
  
  svg {
    font-size: 32px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const BookingCard = styled.div`
  background: #fef4f0;
  margin: 0 24px 24px;
  padding: 24px;
  border-radius: 12px;
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const BookingId = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const BookingIdValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #ef4444;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 14px;
  }
`;

const CoachInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const CoachImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const CoachDetails = styled.div`
  flex: 1;
`;

const CoachName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const CoachTitle = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const DetailItem = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
`;

const DetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  
  svg {
    font-size: 14px;
  }
`;

const DetailValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
`;

const AmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  margin-top: 16px;
`;

const AmountLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const AmountValue = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #10b981;
`;

const WhatsNextSection = styled.div`
  background: #eff6ff;
  margin: 0 24px 24px;
  padding: 24px;
  border-radius: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px;
`;

const NextList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NextItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 12px;
  line-height: 1.5;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    color: #3b82f6;
    font-size: 12px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  padding: 0 24px 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const DashboardButton = styled(Button)`
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const ViewButton = styled(Button)`
  background: #ef4444;
  border: none;
  color: white;
  
  &:hover {
    background: #dc2626;
  }
`;

const Footer = styled.div`
  text-align: center;
  padding: 0 24px 24px;
  font-size: 12px;
  color: #9ca3af;
`;

const sessionTypeLabels = {
  chat: "Chat Session",
  call: "Voice Call",
  video: "Video Session",
};

const BookingSuccessModal = ({ bookingData, onClose }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  // Derive display values from bookingData prop
  const booking = bookingData?.booking;
  const session = bookingData?.session;
  const selectedSessionType = bookingData?.selectedSessionType;
  const selectedSlot = bookingData?.selectedSlot;
  const amount = bookingData?.amount;

  const creatorName = session?.creator_name || booking?.creator_name || "Creator";
  const creatorProfilePic =
    session?.creator?.profile_pic?.trim() ||
    booking?.creator_profile_pic?.trim() ||
    "https://via.placeholder.com/48";
  const creatorDesignation = session?.creator?.designation || "Wellness Coach";
  const bookingId = booking?.id ? `BK${booking.id}` : "—";
  const startTime = booking?.start_time || selectedSlot;
  const duration = session?.duration || booking?.duration || "—";
  const sessionTypeLabel = sessionTypeLabels[selectedSessionType] || selectedSessionType || "Session";

  const formattedDate = startTime
    ? moment.utc(startTime).format("dddd, MMMM D, YYYY")
    : "—";
  const formattedTime = startTime ? moment.utc(startTime).format("h:mm A") : "—";

  const handleDownloadInvoice = async () => {
    if (!booking?.id) return;
    setIsDownloading(true);
    await dispatch(getBookingInvoice(booking.id));
    setIsDownloading(false);
  };

  const handleGoToDashboard = () => {
    if (onClose) onClose();
    history.push("/user_dashboard");
  };

  const handleViewBooking = () => {
    if (onClose) onClose();
    history.push("/user_bookings");
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <SuccessIcon>
            <FaCheckCircle />
          </SuccessIcon>
          <Title>Session Booked Successfully! </Title>
          <Subtitle>
            Your counselling session has been confirmed. You'll receive a confirmation email shortly.
          </Subtitle>
        </Header>

        <BookingCard>
          <BookingHeader>
            <div>
              <BookingId>Booking ID</BookingId>
              <BookingIdValue>{bookingId}</BookingIdValue>
            </div>
            <DownloadButton onClick={handleDownloadInvoice} disabled={isDownloading || !booking?.id}>
              <FaDownload />
              {isDownloading ? "Downloading..." : "Download Invoice"}
            </DownloadButton>
          </BookingHeader>

          <CoachInfo>
            <CoachImage 
              src={creatorProfilePic}
              alt={creatorName}
            />
            <CoachDetails>
              <CoachName>{creatorName}</CoachName>
              <CoachTitle>{creatorDesignation}</CoachTitle>
            </CoachDetails>
          </CoachInfo>

          <DetailsGrid>
            <DetailItem>
              <DetailLabel>
                <FaCalendarAlt />
                Date
              </DetailLabel>
              <DetailValue>{formattedDate}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>
                <FaClock />
                Time
              </DetailLabel>
              <DetailValue>{formattedTime}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>
                <FaComment />
                Type
              </DetailLabel>
              <DetailValue>{sessionTypeLabel}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>
                <FaHourglassHalf />
                Duration
              </DetailLabel>
              <DetailValue>{duration} mins</DetailValue>
            </DetailItem>
          </DetailsGrid>

          <AmountRow>
            <AmountLabel>Amount Paid</AmountLabel>
            <AmountValue>₹{amount ?? "—"}</AmountValue>
          </AmountRow>
        </BookingCard>

        <WhatsNextSection>
          <SectionTitle>What's Next?</SectionTitle>
          <NextList>
            <NextItem>
              <FaCheck />
              You'll receive a confirmation email with session details
            </NextItem>
            <NextItem>
              <FaCheck />
              A reminder will be sent 1 hour before your session
            </NextItem>
            <NextItem>
              <FaCheck />
              Join the session from your dashboard at the scheduled time
            </NextItem>
            <NextItem>
              <FaCheck />
              You can reschedule or cancel up to 2 hours before the session
            </NextItem>
          </NextList>
        </WhatsNextSection>

        <ButtonGroup>
          <DashboardButton onClick={handleGoToDashboard}>
            Go to Dashboard
          </DashboardButton>
          <ViewButton onClick={handleViewBooking}>
            View Booking
            <FaArrowRight />
          </ViewButton>
        </ButtonGroup>

        <Footer>
          Need help? Contact our support team 24/7
        </Footer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default BookingSuccessModal;